<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppDrawer from './components/AppDrawer.vue'
import PWAInstallBanner from './components/PWAInstallBanner.vue'
import BottomNavigation from './components/BottomNavigation.vue'
import DevModeToggle from './components/DevModeToggle.vue'
import { usePWA } from './composables/usePWA'
import { useAuthState } from './composables/useAuthState'
import { useToast } from './composables/useToast'
import { createLogger } from './utils/logger'

const route = useRoute()
const router = useRouter()
const { isInstalled } = usePWA()
const authState = useAuthState()
const { toasts, dismissToast } = useToast()
const logger = createLogger('App')

// Show burger menu only in browser mode (not installed)
const showBurgerMenu = computed(() => {
  return !isInstalled.value
})

// Show bottom navigation only when PWA is installed (regardless of auth state)
const showBottomNav = computed(() => {
  const result = isInstalled.value

  logger.log('🔧 BottomNav visibility:', {
    isInstalled: isInstalled.value,
    hasUser: authState.user.value !== null,
    userType: authState.user.value?.user_type,
    currentPath: route.path,
    showBottomNav: result,
  })
  return result
})

// Helper function to redirect user to dashboard
// Note: All user types now use the unified /dashboard route
const redirectToUserDashboard = async () => {
  const dashboardPath = '/dashboard'
  logger.log(`🔧 Redirecting authenticated user to ${dashboardPath}`)
  await router.push(dashboardPath)
}

// Check if authenticated user on homepage should be redirected
const checkInitialRoute = () => {
  // User is already in authState from localStorage (via useAuthState initialization)
  if (isInstalled.value && authState.user.value && route.path === '/') {
    logger.log('🔧 Authenticated user on homepage, redirecting to dashboard')
    redirectToUserDashboard()
  }
}

// Watch for auth changes (like dev mode user switching)
// Note: authState is already initialized from localStorage, just need to handle live updates
const handleAuthChange = () => {
  logger.log('🔧 Auth change detected')
  // authState will be updated by the component that triggered the change
  // Just check if we need to redirect
  checkInitialRoute()
}

// Watch for PWA mode changes (dev mode toggle)
const handlePWAModeChange = () => {
  logger.log('🔧 PWA mode changed, checking installation status')
  checkInitialRoute()
}

// Handle session expiration
const handleAuthExpired = (event: Event) => {
  const customEvent = event as CustomEvent<{
    message: string
    statusCode: number
  }>
  logger.log('🔒 Session expired, redirecting to login')

  // Get the toast composable
  const { error } = useToast()

  // Show toast notification with message from event
  error(customEvent.detail.message)

  // Redirect to login with current route as redirect parameter
  const currentPath = route.fullPath
  router.replace({
    path: '/login',
    query: { redirect: currentPath !== '/login' ? currentPath : undefined },
  })
}

onMounted(() => {
  // User is already in authState from localStorage initialization
  // No need to block mount with API call
  checkInitialRoute()

  // Listen for storage changes (dev mode user switching)
  window.addEventListener('storage', handleAuthChange)

  // Listen for custom auth events
  window.addEventListener('auth-change', handleAuthChange)

  // Listen for PWA mode changes
  window.addEventListener('pwa-mode-change', handlePWAModeChange)

  // Listen for session expiration
  window.addEventListener('auth-expired', handleAuthExpired)
})

onUnmounted(() => {
  window.removeEventListener('storage', handleAuthChange)
  window.removeEventListener('auth-change', handleAuthChange)
  window.removeEventListener('pwa-mode-change', handlePWAModeChange)
  window.removeEventListener('auth-expired', handleAuthExpired)
})
</script>

<template>
  <v-app>
    <!-- App Bar: Shows burger menu in browser mode, clean bar in installed mode -->
    <AppDrawer :user="authState.user.value" :show-burger="showBurgerMenu" />

    <v-main>
      <RouterView />
    </v-main>

    <PWAInstallBanner v-if="!isInstalled" />
    <BottomNavigation v-if="showBottomNav" :user="authState.user.value" />
    <DevModeToggle />

    <!-- Global Toast Container with Stacking -->
    <div v-for="toast in toasts" :key="toast.id">
      <v-snackbar
        :model-value="toast.show"
        :color="toast.type"
        :timeout="toast.duration"
        :location="toast.position"
        :variant="toast.variant"
        @update:model-value="!$event && dismissToast(toast.id)"
      >
        <div class="d-flex align-center">
          <v-icon v-if="toast.icon !== false" :icon="toast.icon" class="me-3" />
          <span>{{ toast.message }}</span>
        </div>
        <template #actions>
          <v-btn
            v-if="toast.dismissible"
            variant="text"
            @click="dismissToast(toast.id)"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-app>
</template>

<style>
/*
 * Safe Area Support for Devices with Notches and Home Indicators
 *
 * IMPORTANT: These styles require `viewport-fit=cover` to be set in index.html.
 * Without it, safe-area-inset-* values will always be 0px.
 *
 * iOS Support:
 *   - iOS 11.0-11.1: Uses constant() function (deprecated)
 *   - iOS 11.2+: Uses env() function (current standard)
 *
 * See: https://webkit.org/blog/7929/designing-websites-for-iphone-x/
 */
:root {
  /* iOS 11.0-11.1 fallback using constant() */
  --safe-area-inset-top: constant(safe-area-inset-top, 0px);
  --safe-area-inset-right: constant(safe-area-inset-right, 0px);
  --safe-area-inset-bottom: constant(safe-area-inset-bottom, 0px);
  --safe-area-inset-left: constant(safe-area-inset-left, 0px);
  /* iOS 11.2+ and modern browsers using env() */
  --safe-area-inset-top: env(safe-area-inset-top, 0px);
  --safe-area-inset-right: env(safe-area-inset-right, 0px);
  --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
  --safe-area-inset-left: env(safe-area-inset-left, 0px);
}

/* Prevent white bar at bottom of screen on notched devices */
html {
  min-height: calc(100% + var(--safe-area-inset-top));
}

/* Apply safe area to v-app for all content */
.v-application {
  padding-top: var(--safe-area-inset-top);
  padding-left: var(--safe-area-inset-left);
  padding-right: var(--safe-area-inset-right);
}

/* Additional padding for bottom navigation in standalone mode */
@media (display-mode: standalone) {
  /* Add safe area to bottom navigation */
  .v-bottom-navigation {
    /* iOS 11.0-11.1 fallback */
    padding-bottom: constant(safe-area-inset-bottom);
    height: calc(56px + constant(safe-area-inset-bottom)) !important;
    /* iOS 11.2+ and modern browsers */
    padding-bottom: var(--safe-area-inset-bottom);
    height: calc(56px + var(--safe-area-inset-bottom)) !important;
  }

  /* Ensure v-main accounts for safe area bottom when bottom nav is present */
  .v-main {
    padding-bottom: var(--safe-area-inset-bottom) !important;
  }

  /* Optimize for standalone display mode */
  body {
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
  }

  /* Keep form inputs selectable despite user-select: none on body */
  input,
  textarea,
  [contenteditable] {
    -webkit-user-select: text;
    user-select: text;
  }

  /* Prevent pull-to-refresh in PWA */
  body {
    overscroll-behavior-y: contain;
  }
}

/* Ensure app bar respects safe area at top */
.v-app-bar {
  top: var(--safe-area-inset-top) !important;
}
</style>
